/**
 * Timezone-aware date/time utility — @tms-phoenix/ui-vue
 *
 * Contract:
 *   - API always sends/receives UTC ISO-8601 strings.  Never mutate these.
 *   - UI always shows dates/times in the user's configured IANA timezone.
 *   - Standard display format: dd/MM/yyyy HH:mm (24-hour).
 *   - Date-only fields (e.g. DOB stored as YYYY-MM-DD) represent a calendar
 *     date, NOT an instant — format directly without TZ conversion.
 *   - PrimeVue DatePicker operates in browser local time.  Use
 *     `utcIsoToPickerDate` and `pickerDateToUtcIso` to bridge UTC ↔ picker.
 *
 * All functions are pure (no Pinia / Vue dependency) so they can be used
 * safely in both Vue components and Pinia stores.
 */

/** Matches a bare calendar date string YYYY-MM-DD (no time part). */
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

// ── Internal helpers ────────────────────────────────────────────────────────

/**
 * Extract wall-clock components of a UTC instant in a given IANA timezone
 * using Intl.DateTimeFormat.  Returns null on any failure (unsupported TZ,
 * invalid date, etc.) so callers can provide a safe fallback.
 *
 * Why 'en' locale: this is used purely for *parsing* (formatToParts), not for
 * display.  'en' guarantees the numeric fields (year, month, day, hour…) are
 * returned as plain ASCII digits regardless of the user's display language.
 * The actual display format (dd/MM/yyyy HH:mm) is assembled manually below.
 *
 * Why no third-party library: date-fns v4 (installed) has no IANA TZ support,
 * and date-fns-tz / dayjs / luxon are not in the dependency tree.  The native
 * Intl API covers all needs here with zero added weight.
 *
 * Why not use browser local time directly: the user's configured timezone
 * (from the API login response) may differ from the browser's system timezone,
 * so we always pass the IANA TZ explicitly.
 */
function getTzParts(
  utcDate: Date,
  timeZone: string,
): { year: number; month: number; day: number; hour: number; minute: number; second: number } | null {
  try {
    const fmt = new Intl.DateTimeFormat('en', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const parts = fmt.formatToParts(utcDate);
    /** Read a numeric part from the Intl result. */
    const getPart = (type: string, fallback = '0'): number =>
      Number(parts.find((p) => p.type === type)?.value ?? fallback);
    return {
      year: getPart('year'),
      // Intl month is 1-indexed; convert to 0-indexed to match JS Date convention
      month: getPart('month', '1') - 1,
      day: getPart('day', '1'),
      // Some Intl implementations return '24' for midnight — normalise hours only
      hour: getPart('hour') % 24,
      minute: getPart('minute'),
      second: getPart('second'),
    };
  } catch {
    return null;
  }
}

const pad = (n: number): string => String(n).padStart(2, '0');

// ── Display formatting ──────────────────────────────────────────────────────

/**
 * Format a UTC ISO-8601 string as `dd/MM/yyyy HH:mm` in the user's timezone.
 *
 * Returns `'—'` for null / undefined / invalid input.
 */
export function formatDateTime(
  utcIso: string | null | undefined,
  timeZone: string,
): string {
  if (!utcIso) return '—';
  try {
    const d = new Date(utcIso);
    if (isNaN(d.getTime())) return '—';
    const p = getTzParts(d, timeZone);
    if (!p) return '—';
    return `${pad(p.day)}/${pad(p.month + 1)}/${p.year} ${pad(p.hour)}:${pad(p.minute)}`;
  } catch {
    return '—';
  }
}

/**
 * Format a date value as `dd/MM/yyyy` in the user's timezone.
 *
 * - Bare date strings (`YYYY-MM-DD`) represent a calendar date: formatted
 *   directly with no timezone conversion (avoids day-boundary shifts for
 *   DOB, milk-start-date, etc.).
 * - UTC ISO timestamps are converted to the user's timezone first.
 *
 * Returns `'—'` for null / undefined / invalid input.
 */
export function formatDate(
  value: string | null | undefined,
  timeZone: string,
): string {
  if (!value) return '—';
  try {
    if (DATE_ONLY_RE.test(value)) {
      // Calendar date — no TZ conversion
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    }
    // Timestamp — convert to user TZ
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    const p = getTzParts(d, timeZone);
    if (!p) return '—';
    return `${pad(p.day)}/${pad(p.month + 1)}/${p.year}`;
  } catch {
    return '—';
  }
}

// ── PrimeVue DatePicker bridge ──────────────────────────────────────────────

/**
 * Convert a UTC ISO string to a `Date` value for PrimeVue DatePicker.
 *
 * PrimeVue DatePicker renders its value in browser local time.  This
 * function creates a "shifted" Date whose browser-local representation
 * matches the wall-clock time of the UTC instant in the user's timezone.
 *
 * Example:
 *   UTC `2024-01-15T10:00:00Z`, user TZ = `America/New_York` (UTC-5)
 *   → picker should show `15/01/2024 05:00`
 *   → returns `new Date(2024, 0, 15, 5, 0, 0)` (browser local = 05:00)
 *
 * Returns `null` for null / undefined / invalid input.
 */
export function utcIsoToPickerDate(
  utcIso: string | null | undefined,
  timeZone: string,
): Date | null {
  if (!utcIso) return null;
  try {
    const d = new Date(utcIso);
    if (isNaN(d.getTime())) return null;
    const p = getTzParts(d, timeZone);
    if (!p) return null;
    // Construct using browser local time so the picker shows user TZ wall clock
    return new Date(p.year, p.month, p.day, p.hour, p.minute, p.second);
  } catch {
    return null;
  }
}

/**
 * Convert a DatePicker-selected `Date` back to a UTC ISO string.
 *
 * The picker value uses browser local time which, by convention, represents
 * the user's configured timezone wall clock.  This function interprets the
 * local components as user-TZ time and converts to UTC.
 *
 * Returns `undefined` for null / undefined input.
 */
export function pickerDateToUtcIso(
  pickerDate: Date | null | undefined,
  timeZone: string,
): string | undefined {
  if (!pickerDate) return undefined;
  try {
    // Extract wall-clock components from the picker (browser local = user TZ wall clock)
    const year = pickerDate.getFullYear();
    const month = pickerDate.getMonth(); // 0-indexed
    const day = pickerDate.getDate();
    const hour = pickerDate.getHours();
    const minute = pickerDate.getMinutes();
    const second = pickerDate.getSeconds();

    // Treat these components as UTC to build an initial approximation
    const naiveUtcMs = Date.UTC(year, month, day, hour, minute, second);

    // Fixed-point iteration: re-probe the offset at the *corrected* UTC instant
    // and repeat until it stabilises.  A single-pass approximation can be wrong
    // when the naive probe crosses a DST boundary (the offset at the probe time
    // differs from the offset at the intended wall-clock time).  Two iterations
    // are sufficient for all real-world DST rules; three provide a safety margin.
    let utcMs = naiveUtcMs;
    for (let i = 0; i < 3; i++) {
      const probe = new Date(utcMs);
      const p = getTzParts(probe, timeZone);
      if (!p) return undefined;
      const tzAsUtcMs = Date.UTC(p.year, p.month, p.day, p.hour, p.minute, p.second);
      const offsetMs = tzAsUtcMs - utcMs;
      const nextUtcMs = naiveUtcMs - offsetMs;
      if (nextUtcMs === utcMs) break; // offset has stabilised — done
      utcMs = nextUtcMs;
    }

    return new Date(utcMs).toISOString();
  } catch {
    return undefined;
  }
}

// ── datetime-local input helpers ────────────────────────────────────────────

/**
 * Convert a UTC ISO string to `YYYY-MM-DDTHH:mm` in the user's timezone.
 *
 * Use this to set the `value` of a `<input type="datetime-local">` when the
 * user should see/edit time in their configured timezone (not browser local).
 *
 * Returns `''` for null / undefined / invalid input.
 */
export function utcIsoToDatetimeLocal(
  utcIso: string | null | undefined,
  timeZone: string,
): string {
  if (!utcIso) return '';
  try {
    const d = new Date(utcIso);
    if (isNaN(d.getTime())) return '';
    const p = getTzParts(d, timeZone);
    if (!p) return '';
    return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
  } catch {
    return '';
  }
}

/**
 * Return `now + offsetHours` formatted as `YYYY-MM-DDTHH:mm` in the user's
 * timezone.  Designed for `<input type="datetime-local">` default values
 * such as container expiry timestamps.
 */
export function nowPlusHoursInUserTz(offsetHours: number, timeZone: string): string {
  const d = new Date(Date.now() + offsetHours * 3_600_000);
  const p = getTzParts(d, timeZone);
  if (!p) {
    // Fallback: browser local time
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
}

// ── "Now" / "Today" helpers ─────────────────────────────────────────────────

/**
 * Current moment as a UTC ISO string.  Always UTC — safe to send to APIs.
 */
export function nowUtcIso(): string {
  return new Date().toISOString();
}

/**
 * Current date as `YYYY-MM-DD` in the user's timezone.
 *
 * Use instead of `new Date().toISOString().slice(0, 10)` (which is UTC and
 * can differ from the user's local date near midnight).
 */
export function todayInUserTz(timeZone: string): string {
  const now = new Date();
  const p = getTzParts(now, timeZone);
  if (!p) return now.toISOString().slice(0, 10);
  return `${p.year}-${pad(p.month + 1)}-${pad(p.day)}`;
}

/**
 * Returns `true` when a `YYYY-MM-DD` string is strictly before today in the
 * user's timezone.  Useful for expiry / past-date checks.
 */
export function isBeforeToday(dateStr: string, timeZone: string): boolean {
  return dateStr < todayInUserTz(timeZone);
}

// ── Date-only / DatePicker helpers ──────────────────────────────────────────

/**
 * Parse a bare calendar date string `YYYY-MM-DD` into a DatePicker-compatible
 * Date by constructing a browser-local midnight.
 *
 * Use instead of `new Date('YYYY-MM-DD')`, which parses as UTC midnight and
 * can shift the displayed date by one day in UTC+ timezones.
 *
 * Returns `null` for null / undefined / non-`YYYY-MM-DD` input.
 */
export function parseDateOnlyToPickerDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr || !DATE_ONLY_RE.test(dateStr)) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d); // browser-local midnight
}

/**
 * Convert a DatePicker-selected `Date` to a bare calendar date string
 * `YYYY-MM-DD` by reading the browser-local date components.
 *
 * Use instead of `d.toISOString().split('T')[0]`, which gives the UTC date
 * and can return the previous calendar day for UTC+ timezone users.
 *
 * Returns `undefined` for null / undefined input.
 */
export function pickerDateToDateOnly(pickerDate: Date | null | undefined): string | undefined {
  if (!pickerDate) return undefined;
  return `${pickerDate.getFullYear()}-${pad(pickerDate.getMonth() + 1)}-${pad(pickerDate.getDate())}`;
}
